import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import { useEffect, useState } from "react";
import { useFundraiser } from "../../pages/ImprovedShopPage/FundraiserContext";
import { useCart } from "../../contexts/Cart/CartContext";
import heroImg from "../../images/ImprovedFundraiserPage/hero-img.png";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import silhoutte from "../../images/headshot-silhouette-30.png";

import { ImprovedParticipantShop } from "../../Interfaces/FundraiserRole";
import Countdown from "./Countdown";
import { LeaderboardEntries } from "../Storefront/ParticipantProfileUI";
import config from "../../config";
import ShareButton from "../Storefront/ShareStoreComponent";

interface FundraiserHeaderProps {
  participantShop: ImprovedParticipantShop;
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  isFromSamplePage?: boolean;
}

const FundraiserHeader: React.FC<FundraiserHeaderProps> = ({
  participantShop,
  timeLeft,
  isFromSamplePage = false, //if not passed, assume it's not from sample page and use real data
}) => {
  const { data, loading, error } = useFundraiser();
  const { state: cartState } = useCart();
  const cartItemCount =
    cartState?.cart?.reduce(
      (total: number, item: { quantity?: number }) =>
        total + (item.quantity || 0),
      0,
    ) || 0;

  if (loading) {
    return (
      <section>
        <div className="hero-bg h-100">
          <div className="container mx-auto d-flex justify-content-center align-items-center p-4 py-5">
            <div className="text-center">
              <p className="h4 text-white">Loading...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !participantShop) {
    return (
      <section>
        <div className="hero-bg h-100">
          <div className="container mx-auto d-flex justify-content-center align-items-center p-4 py-5">
            <div className="text-center">
              <p className="h4 text-danger">Error loading fundraiser data</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="hero-bg h-100">
        <div className="py-5 px-3">
          <div className="row g-4 justify-content-center align-items-stretch">
            {/* Participant Card */}
            <div className="col-12 col-lg-7 d-flex">
              <div className="rounded-4 p-4 w-100 d-flex flex-column justify-content-between card-bg">
                <div className="row g-0 align-items-start">
                  {/* Image */}
                  <div className="col-12 col-md-4 d-flex flex-column align-items-center align-items-md-start mb-3 mb-md-0">
                    <img
                      src={(() => {
                        const profileImageUrl =
                          participantShop.participant?.profile_image_url;
                        if (!profileImageUrl) return silhoutte;
                        return profileImageUrl;
                      })()}
                      alt={`${participantShop.participant?.first_name} ${participantShop.participant?.last_name}`}
                      className="img-fluid rounded-3"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                    <div className="d-flex gap-2 w-100 mt-4 flex-column flex-xxl-row">
                      <ShareButton
                        participantID={
                          participantShop.participant.participant_code
                        }
                        organizationName={`${participantShop.fundraiser.org_name} ${participantShop.fundraiser.fundraiser_name}`}
                      />
                    </div>
                  </div>
                  {/* Info */}
                  <div className="col-12 col-md-8 ps-md-4 d-flex flex-column justify-content-between">
                    <div>
                      <h1 className="h1 fw-bold font-fraunces text-primary-custom mb-1">
                        {participantShop.participant.first_name}{" "}
                        {participantShop.participant.last_name}
                      </h1>
                      <p className="fs-6 mb-3 text-dark-custom">
                        {`${participantShop.fundraiser.org_name} ${participantShop.fundraiser.fundraiser_name}`}
                      </p>
                      <p className="fs-5 mb-1">
                        {participantShop.fundraiser.fundraiser_purpose_message}
                      </p>
                      <p className="fs-5 fw-semibold text-primary-custom mb-3">
                        Your support means the world!
                      </p>
                    </div>
                    <div className="w-100">
                      <div
                        className="w-100 rounded-pill my-2 progress-bg"
                        style={{ background: "#dbead7", height: "10px" }}
                      >
                        <div
                          className="rounded-pill transition-all progress-fill"
                          style={{
                            width: `${Math.min(
                              (participantShop.participant.current_profit /
                                participantShop.participant.current_goal) *
                                100,
                              100,
                            )}%`,
                            background: "#2b6e5a",
                            height: "10px",
                          }}
                        ></div>
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <p className="fs-4 fw-semibold text-primary-custom mb-0">
                          $
                          {participantShop.participant.current_profit.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}{" "}
                          <span className="text-dark-custom">Raised</span>
                        </p>
                        <p className="fs-6 text-secondary fw-semibold mb-0">
                          Goal: $
                          {participantShop.participant.current_goal.toLocaleString(
                            "en-US",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-5 d-flex">
              <LeaderboardEntries
                fundraiser_id={participantShop.fundraiser.id}
                isFromSamplePage={isFromSamplePage}
                primary_color={
                  participantShop.fundraiser.primary_organization_hex ||
                  "#FFFFFF"
                }
              />
            </div>
          </div>
        </div>
      </div>
      {/* Countdown Bar */}
      <Countdown timeLeft={timeLeft} />
    </section>
  );
};

export default FundraiserHeader;
